import React, { useState, useCallback, useMemo, useImperativeHandle, forwardRef, useEffect } from 'react';
import { fetchPatientSearchCatalog, fetchPatients } from '../api';
import debounce from 'lodash/debounce';
import { Link } from 'react-router-dom';
import {
  clearPatientSearchCache,
  debugSearchPatientCatalog,
  getPatientSearchCacheStatus,
  searchCachedPatientCatalog,
  warmPatientSearchCatalog
} from './patientSearchCache';
import './PacientSearchSelect.css';

const PacientSearchSelect = forwardRef(({ onPatientSelect, selectedPatient: selectedPatientProp }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPatientState, setSelectedPatientState] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Expose resetSearch method to parent component
  useImperativeHandle(ref, () => ({
    resetSearch: () => {
      setSearchTerm('');
      setPatients([]);
      setSelectedPatientState(null);
      setHasSearched(false);
    }
  }));

  useEffect(() => {
    if (selectedPatientProp) {
      setSelectedPatientState(selectedPatientProp);
    }
  }, [selectedPatientProp]);

  useEffect(() => {
    window.patientSearchCache = {
      status: getPatientSearchCacheStatus,
      search: debugSearchPatientCatalog,
      warm: () => warmPatientSearchCatalog(fetchPatientSearchCatalog),
      clear: clearPatientSearchCache,
    };

    const preloadTimer = setTimeout(() => {
      warmPatientSearchCatalog(fetchPatientSearchCatalog);
    }, 500);

    return () => clearTimeout(preloadTimer);
  }, []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce(async (term) => {
      if (!term || term.length < 3) {
        setPatients([]);
        setHasSearched(false);
        return;
      }

      setIsLoading(true);
      try {
        const { results: cachedResults, source } = searchCachedPatientCatalog(term, { limit: 10 });
        const results = source === 'cache' && cachedResults.length > 0
          ? cachedResults
          : await fetchPatients(term);

        if (source !== 'cache') {
          warmPatientSearchCatalog(fetchPatientSearchCatalog);
        }

        setPatients(results);
        setHasSearched(true);
      } catch (error) {
        console.error('Patient search error:', error);
        setPatients([]);
        setHasSearched(true);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  // Memoized search handler
  const handleSearch = useCallback((e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // If a patient was selected, clicking to type should clear the selection
    if (selectedPatientState) {
      setSelectedPatientState(null);
    }
    
    debouncedSearch(term);
  }, [debouncedSearch, selectedPatientState]);

  // Memoized patient selection
  const handlePatientSelection = useCallback(async (patient) => {
    setIsLoading(true);
    try {
      const fullPatientResults = patient.pacientId
        ? await fetchPatients({ pacient_id: patient.pacientId })
        : [];
      const selectedPatient = fullPatientResults[0] || patient;

      onPatientSelect(selectedPatient);
      setSelectedPatientState(selectedPatient);
      setSearchTerm('');
      setPatients([]);
    } catch (error) {
      console.error('Patient selection fetch error:', error);
      onPatientSelect(patient);
      setSelectedPatientState(patient);
      setSearchTerm('');
      setPatients([]);
    } finally {
      setIsLoading(false);
    }
  }, [onPatientSelect]);

  return (
    <div className="patient-search-container">
      <div className="search-input-container">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder={selectedPatientState ? selectedPatientState.Nome : "Digite o nome do paciente"}
          className="search-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck="false"
          name="patient-search"
        />
      </div>

      {isLoading && <div className="loading-indicator">Carregando...</div>}

      {patients.length > 0 && (
        <div className="patient-dropdown">
          {patients.map((patient) => (
            <div
              key={patient.pacientId} 
              onClick={() => handlePatientSelection(patient)}
              className="patient-option"
            >
              <span className="patient-name">{patient.Nome}</span>
              {patient.CPF && <span className="patient-cpf">CPF: {patient.CPF}</span>}
            </div>
          ))}
          <Link to="/pacientes" className="patient-option new-patient-option">
            <span className="patient-name">Cadastrar novo paciente</span>
          </Link>
        </div>
      )}

      {hasSearched && !isLoading && patients.length === 0 && !selectedPatientState && (
        <div className="no-results">
          <div>Nenhum paciente encontrado</div>
          <Link to="/pacientes" className="new-patient-link">
            Cadastrar novo paciente +
          </Link>
        </div>
      )}
    </div>
  );
});

export default PacientSearchSelect;
